// ATA Chapters — row type.
//
// Source of truth: the backend READ schema, NOT the wire's full field list.
// `GET /v6/ata_chapters` serves `Page[schemas.ATAChapterList]`, and that schema
// declares FIFTEEN members — but only these seven are columns on the SQLAlchemy
// model (app/ata_chapters/models.py):
//
//   id · major_code · minor_code · ata_major_title · ata_title · ata_desc · ata_status
//
// The other eight — chapter_code, code, number, title, name, category, group,
// description, active — are Python `@property` accessors on the model, computed
// from the columns above (e.g. `chapter_code` = "05-20", `title`/`name` both
// return `ata_title`, `category`/`group` both return `ata_major_title`).
//
// They DO serialize onto the wire, so binding a grid column to one renders
// correctly and looks entirely fine. But `get_sort()`/`get_filters()` resolve a
// field name to a SQL column, and a property has none — so the first click on
// that header, or any filter on it, fails the whole list. That is why they are
// deliberately absent from this interface: an unsortable column in a
// 483-row reference table is a trap, and `major_code` + `minor_code` carry the
// same information as `chapter_code` while sorting correctly.
//
// Verified against the live g2 spec + module_g2_demo on 2026-08-23.

export interface ATAChapterRow {
  id: number;
  /** ATA major chapter, zero-padded 2 chars ("05", "11", "72"). Sorts lexically. */
  major_code: string | null;
  /** ATA sub-chapter, zero-padded 2 chars. "00" on the 46 chapter-header rows. */
  minor_code: string | null;
  /** Major chapter name, e.g. "TIME LIMITS/MAINTENANCE CHECKS". Uppercase in the data. */
  ata_major_title: string | null;
  /** Sub-chapter name, e.g. "Maintenance Checks". */
  ata_title: string | null;
  /** Long-form guidance. Populated on 480 of 483 rows; "" on the other 3. */
  ata_desc: string | null;
  /**
   * "Active" on all 483 rows in module_g2_demo (measured 2026-08-23), so it is
   * carried on the type but given no column — a constant column is noise. It is
   * a real, sortable column if a tenant ever varies it.
   */
  ata_status: string | null;
}

/**
 * A record's ATA chapter as the record serves it: a component's (its part's
 * parts.ata_chapter_id) and an inspection's (aircraft_inspections.ata_chapter_id,
 * version-25). Backend: app/ata_chapters/linking.py chapter_ref. `code` is "62-10",
 * or "62" for a chapter heading (minor "00"). Null where the record has none, or the
 * tenant's DB has no column yet.
 */
export interface AtaChapterRef {
  id: number;
  code: string | null;
  title: string | null;
}

/** "62-10 Main Rotor Blades" -- the one label a chapter shows wherever it appears. */
export function ataChapterLabel(chapter: Pick<AtaChapterRef, "code" | "title"> | null | undefined): string {
  if (!chapter) return "";
  return [chapter.code, chapter.title].filter((s) => s && String(s).trim()).join(" ");
}

/** A chapter list row as the AtaChapterRef a record serves (linking.chapter_ref's code rule). */
export function chapterRefOf(row: ATAChapterRow): AtaChapterRef {
  const major = row.major_code ?? "";
  const minor = row.minor_code ?? "";
  const code = major && minor && minor !== "00" ? `${major}-${minor}` : major;
  return { id: row.id, code: code || null, title: row.ata_title };
}
